import {HttpErrorResponse} from '@angular/common/http';

/**
 * Typed HTTP failure wrapper (AC-25).
 * Prefer mapping interceptor / catchError handlers through `HttpAppError.from`
 * instead of reading raw `HttpErrorResponse` fields ad hoc.
 */
export class HttpAppError extends Error {
  readonly status: number;
  readonly url: string | null;
  readonly body: unknown;
  readonly original: HttpErrorResponse;

  constructor(status: number, message: string, opts: {
    url?: string | null;
    body?: unknown;
    original: HttpErrorResponse;
  }) {
    super(message);
    this.name = 'HttpAppError';
    this.status = status;
    this.url = opts.url ?? null;
    this.body = opts.body;
    this.original = opts.original;
  }

  get isUnauthorized(): boolean {
    return this.status === 401;
  }

  get isForbidden(): boolean {
    return this.status === 403;
  }

  static from(err: HttpErrorResponse): HttpAppError {
    const body = err.error;
    const message =
      (body && typeof body === 'object' && (body.message || body.error)) ||
      (typeof body === 'string' && body) ||
      err.message ||
      `HTTP ${err.status}`;
    return new HttpAppError(err.status, String(message), {
      url: err.url,
      body,
      original: err,
    });
  }
}

/** Flatten Nest/MySQL `{ message }` wrappers into a single string. */
export function httpErrorMessage(err: unknown, fallback = 'Request failed'): string {
  const app = mapToHttpAppError(err);
  const fromBody = flattenMessage(app?.body);
  if (fromBody) {
    return fromBody;
  }
  const fromApp = flattenMessage(app?.message);
  if (fromApp) {
    return fromApp;
  }
  if (err && typeof err === 'object' && 'message' in err) {
    const fromErr = flattenMessage((err as {message?: unknown}).message);
    if (fromErr) {
      return fromErr;
    }
  }
  return fallback;
}

function flattenMessage(value: unknown, depth = 0): string {
  if (depth > 4 || value == null) {
    return '';
  }
  if (typeof value === 'string') {
    const text = value.trim();
    return !text || text === '[object Object]' ? '' : text;
  }
  if (Array.isArray(value) && value.length) {
    return flattenMessage(value[0], depth + 1);
  }
  if (typeof value === 'object' && 'message' in (value as object)) {
    return flattenMessage((value as {message?: unknown}).message, depth + 1);
  }
  return '';
}

export function mapToHttpAppError(err: unknown): HttpAppError | null {
  if (err instanceof HttpAppError) {
    return err;
  }
  if (err instanceof HttpErrorResponse) {
    return HttpAppError.from(err);
  }
  return null;
}
